import DiseaseEncyclopediaModel, {
  EDiseaseEncyclopediaBottomButtonType,
  DiseaseEncyclopediaBannerModel,
  DiseaseEncyclopediaFaqModel,
} from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { exhaustiveGuard } from '@/shared/utils/typeGuard';
import { DiseaseEncyclopediaBottomButtonType, DiseaseEncyclopediaByUserQuery } from '@/types/bff/gql-schema';
import { QueryClient } from '@tanstack/react-query';
import { useDiseaseEncyclopediaByUserQuery } from './disease-encyclopedia.gql.impl';

const parseBottomButtonType = (bottomButtonType: DiseaseEncyclopediaBottomButtonType) => {
  switch (bottomButtonType) {
    case DiseaseEncyclopediaBottomButtonType.DiagnosisReservation:
      return EDiseaseEncyclopediaBottomButtonType.DiagnosisReservation;
    case DiseaseEncyclopediaBottomButtonType.None:
      return EDiseaseEncyclopediaBottomButtonType.None;
    case DiseaseEncyclopediaBottomButtonType.Qna:
      return EDiseaseEncyclopediaBottomButtonType.Qna;
    default:
      return exhaustiveGuard(bottomButtonType);
  }
};

const parseDiseaseEncyclopedia = (data: DiseaseEncyclopediaByUserQuery) => {
  return new DiseaseEncyclopediaModel({
    bottomButtonType: parseBottomButtonType(data.diseaseEncyclopediaByUser.bottomButtonType),
    content: data.diseaseEncyclopediaByUser.content,
    departmentTags: data.diseaseEncyclopediaByUser.departmentTags,
    id: data.diseaseEncyclopediaByUser.id,
    publicId: data.diseaseEncyclopediaByUser.publicId,
    searchTags: data.diseaseEncyclopediaByUser.searchTags,
    title: data.diseaseEncyclopediaByUser.title,
    titleImageUrl: data.diseaseEncyclopediaByUser.titleImageUrl,
    banner: data.diseaseEncyclopediaByUser.banner
      ? new DiseaseEncyclopediaBannerModel({
          buttonPhrase: data.diseaseEncyclopediaByUser.banner?.buttonPhrase,
          description: data.diseaseEncyclopediaByUser.banner?.description,
          externalUrl: data.diseaseEncyclopediaByUser.banner?.externalUrl,
          imageUrl: data.diseaseEncyclopediaByUser.banner?.imageUrl,
          title: data.diseaseEncyclopediaByUser.banner?.title,
        })
      : undefined,
    symptomTags: data.diseaseEncyclopediaByUser.symptomTags,
    faqs: data.diseaseEncyclopediaByUser.faqs.map(
      (faq) =>
        new DiseaseEncyclopediaFaqModel({
          question: faq.question,
          answer: faq.answer,
        })
    ),
  });
};

const useDiseaseEncyclopediaByUser = (diseaseEncyclopediaPid: string) => {
  const { data: diseaseEncyclopedia } = useDiseaseEncyclopediaByUserQuery(
    {
      diseaseEncyclopediaPid,
    },
    {
      enabled: !!diseaseEncyclopediaPid,
      select: parseDiseaseEncyclopedia,
      staleTime: 1000 * 60 * 5, // 5분 - SSR과 동일한 설정
    }
  );

  return {
    diseaseEncyclopedia,
  };
};

export default useDiseaseEncyclopediaByUser;

useDiseaseEncyclopediaByUser.prefetchQuery = async (queryClient: QueryClient, diseaseEncyclopediaPid: string) => {
  return queryClient.prefetchQuery({
    queryKey: useDiseaseEncyclopediaByUserQuery.getKey({ diseaseEncyclopediaPid }),
    queryFn: useDiseaseEncyclopediaByUserQuery.fetcher({ diseaseEncyclopediaPid }),
    staleTime: 1000 * 60 * 5, // 5분
  });
};

useDiseaseEncyclopediaByUser.getKey = (diseaseEncyclopediaPid: string) => {
  return useDiseaseEncyclopediaByUserQuery.getKey({ diseaseEncyclopediaPid });
};
